/**
 * solarCalc — NOAA sunrise-equation solar times for the Day View's FAA
 * night indicators. Pure math, no network, ±1 min accuracy.
 *
 * Three FAA "night" definitions this feeds (see docs/day-view-migration.md):
 *   1. Sunset → sunrise                       — position lights (14 CFR 91.209)
 *   2. Civil dusk → civil dawn                — loggable night   (14 CFR 1.1)
 *   3. Sunset+1h → sunrise−1h                 — night pax currency (14 CFR 61.57(b))
 *
 * Zeniths: 90.833° = official sunrise/sunset (refraction + solar radius);
 * 96° = civil twilight (dawn/dusk). Civil twilight duration is COMPUTED per
 * location/date — never a hardcoded offset (Hawaii ≈ 25 min; high latitudes
 * 40+ min).
 *
 * All returned instants are UTC epoch SECONDS for the given calendar date;
 * the caller converts into the schedule's display timezone. Polar edge case:
 * cosH out of [−1, 1] → `polarDay` (sun never sets) / `polarNight` (never
 * rises) and null instants.
 *
 * Results are memoized per (lat, lng, y-m-d).
 *
 * @module solarCalc
 */

const DEG = Math.PI / 180;
const sinD = d => Math.sin(d * DEG);
const cosD = d => Math.cos(d * DEG);
const tanD = d => Math.tan(d * DEG);
const asinD = v => Math.asin(v) / DEG;
const acosD = v => Math.acos(v) / DEG;
const atanD = v => Math.atan(v) / DEG;
const mod = (n, m) => ((n % m) + m) % m;

export const ZENITH_OFFICIAL = 90.833;
export const ZENITH_CIVIL = 96;

/** Day of year (1-based) for a UTC calendar date. */
function dayOfYear(y, m, d) {
	return Math.floor((Date.UTC(y, m - 1, d) - Date.UTC(y, 0, 1)) / 86400000) + 1;
}

/**
 * Core NOAA algorithm: UTC epoch seconds of one solar event, or the string
 * 'polarDay' / 'polarNight' when the event does not occur at that latitude
 * on that date for the given zenith.
 *
 * @param {number} lat   Degrees, north positive.
 * @param {number} lng   Degrees, EAST positive (Hawaii ≈ −156).
 * @param {number} y     Full year.
 * @param {number} m     Month 1–12.
 * @param {number} d     Day of month.
 * @param {boolean} rising  true = sunrise-type event, false = sunset-type.
 * @param {number} zenith   Degrees (90.833 official, 96 civil).
 * @returns {number|'polarDay'|'polarNight'}
 */
export function solarEventUTC(lat, lng, y, m, d, rising, zenith) {
	const N = dayOfYear(y, m, d);
	const lngHour = lng / 15;
	const t = rising ? N + (6 - lngHour) / 24 : N + (18 - lngHour) / 24;

	// Sun's mean anomaly → true longitude.
	const M = 0.9856 * t - 3.289;
	const L = mod(M + 1.916 * sinD(M) + 0.02 * sinD(2 * M) + 282.634, 360);

	// Right ascension, quadrant-adjusted into L's quadrant, then in hours.
	let RA = mod(atanD(0.91764 * tanD(L)), 360);
	RA += Math.floor(L / 90) * 90 - Math.floor(RA / 90) * 90;
	RA /= 15;

	// Declination.
	const sinDec = 0.39782 * sinD(L);
	const cosDec = cosD(asinD(sinDec));

	// Local hour angle at the requested zenith.
	const cosH = (cosD(zenith) - sinDec * sinD(lat)) / (cosDec * cosD(lat));
	if (cosH > 1) return 'polarNight'; // sun never reaches the zenith (never rises)
	if (cosH < -1) return 'polarDay'; // sun never drops below it (never sets)

	const H = (rising ? 360 - acosD(cosH) : acosD(cosH)) / 15;

	// Local MEAN time of the event on the given LOCAL calendar date, then to
	// UTC by removing the longitude offset. Crucially the mod(24) is applied to
	// the LOCAL time (anchoring the event to local date `d`) BEFORE the
	// longitude shift — wrapping the UTC value instead drops the date rollover
	// for far-west longitudes (a Hawaiian sunset is on the NEXT UTC day) and
	// yielded sunset epochs 24h early (negative day lengths).
	const T = H + RA - 0.06571 * t - 6.622;
	const Tlocal = mod(T, 24);

	return Math.round(Date.UTC(y, m - 1, d) / 1000 + (Tlocal - lngHour) * 3600);
}

const cache = new Map();

/**
 * All four instants for one location + calendar date.
 *
 * @param {number} lat
 * @param {number} lng  East-positive degrees.
 * @param {number} y
 * @param {number} m    1–12.
 * @param {number} d
 * @returns {{
 *   dawnUTC: number|null, sunriseUTC: number|null,
 *   sunsetUTC: number|null, duskUTC: number|null,
 *   polarDay: boolean, polarNight: boolean
 * }}
 */
export function sunTimesUTC(lat, lng, y, m, d) {
	const key = `${lat},${lng},${y}-${m}-${d}`;
	const hit = cache.get(key);
	if (hit) return hit;

	const sunrise = solarEventUTC(lat, lng, y, m, d, true, ZENITH_OFFICIAL);
	const sunset = solarEventUTC(lat, lng, y, m, d, false, ZENITH_OFFICIAL);
	const dawn = solarEventUTC(lat, lng, y, m, d, true, ZENITH_CIVIL);
	const dusk = solarEventUTC(lat, lng, y, m, d, false, ZENITH_CIVIL);

	const num = v => (typeof v === 'number' ? v : null);
	const out = {
		dawnUTC: num(dawn),
		sunriseUTC: num(sunrise),
		sunsetUTC: num(sunset),
		duskUTC: num(dusk),
		polarDay: sunrise === 'polarDay' || sunset === 'polarDay',
		polarNight: sunrise === 'polarNight' || sunset === 'polarNight'
	};
	cache.set(key, out);
	return out;
}

export default sunTimesUTC;
